<template>
  <div>
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <!-- Loading state -->
        <div
          v-if="isLoading"
          class="cause-icon loading-icon"
          v-bind="attrs"
          v-on="on"
          :style="iconStyle"
        >
            <v-icon color="white" size="24">mdi mdi-heart-outline</v-icon>
        </div>
        <!-- SVG content -->
        <div
          v-else-if="compiledSvg"
          v-html="compiledSvg"
          class="cause-icon"
          v-bind="attrs"
          v-on="on"
          :style="iconStyle"
        ></div>
        <!-- Fallback for when SVG is not loaded -->
        <div
          v-else
          class="cause-icon fallback-icon"
          v-bind="attrs"
          v-on="on"
          :style="iconStyle"
        >
          <span class="fallback-text"> <v-icon color="white" size="24">mdi mdi-heart-outline</v-icon></span>
        </div>
      </template>
      <span>{{ name }}</span>
    </v-tooltip>
  </div>
</template>
<script>
import axios from "axios";
import { mapActions } from "vuex";
export default {
  props: {
    name: String,
    type: String,
    size: {
      default: "60"
    }
  },
  data() {
    return {
      compiledSvg: "",
      icon: {},
      isLoading: false
    };
  },
  computed: {
    iconStyle() {
      const padding = this.$vuetify.breakpoint.xs && this.size == '30' 
        ? '3' 
        : parseInt(this.size) / 5;
      
      const baseStyle = {
        fill: this.icon.color || '#ccc',
        borderColor: this.icon.color || '#ccc',
        width: this.size + 'px',
        height: this.size + 'px',
        borderRadius: parseInt(this.size) / 2 + 'px',
        padding: padding + 'px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      };

      // Only add background color for fallback and loading states
      if (!this.compiledSvg || this.isLoading) {
        baseStyle.backgroundColor = this.icon.color || '#ccc';
      }

      return baseStyle;
    }
  },
  watch: {
    name(newValue) {
      this.getIcon({ type: this.type, name: newValue })
        .then(res => {
          this.icon = res;
          this.loadFile();
        })
        .catch(error => {
          console.log(error);
        });
    }
  },

  methods: {
    ...mapActions("icon", {
      getIcon: "getIcon"
    }),
    loadFile() {
      if (!this.icon.image) {
        this.compiledSvg = "";
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      
      // First try to load as SVG with proper headers
      axios({
        method: "get",
        url: this.icon.image,
        headers: {
          'Accept': 'image/svg+xml, text/plain, */*'
        },
        timeout: 10000 // 10 second timeout
      })
        .then(result => {
      
          // Ensure we have valid SVG content
          if (typeof result.data === 'string' && result.data.includes('<svg')) {
            // Clean up the SVG and ensure it has proper fill inheritance
            let svgContent = result.data;
            
            // Remove any hardcoded fill colors to allow CSS styling
            svgContent = svgContent.replace(/fill="[^"]*"/g, 'fill="currentColor"');
            
            this.compiledSvg = svgContent;
          } else {
            console.warn('Invalid SVG content received for SiteIcon, trying as regular image');
            // If it's not valid SVG, we'll let the browser handle it as an image
            this.compiledSvg = `<img src="${this.icon.image}" style="width: 100%; height: 100%; object-fit: contain;" alt="${this.name}" />`;
          }
          this.isLoading = false;
        })
        .catch(error => {
          console.error("Error loading SVG file for SiteIcon:", error);
          
          // Fallback: try to load as regular image
          this.compiledSvg = `<img src="${this.icon.image}" style="width: 100%; height: 100%; object-fit: contain;" alt="${this.name}" onerror="this.style.display='none'" />`;
          this.isLoading = false;
        });
    },
    createFallbackIcon() {
      // Create a simple SVG fallback with the specified color
      const fallbackSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100%" height="100%">
          <circle cx="50" cy="50" r="45" fill="${this.icon.color || '#ccc'}" stroke="${this.icon.color || '#ccc'}" stroke-width="2"/>
          <text x="50" y="55" font-family="Arial, sans-serif" font-size="20" fill="white" text-anchor="middle" dominant-baseline="middle">
            ${this.name ? this.name.charAt(0).toUpperCase() : '?'}
          </text>
        </svg>
      `;
      this.compiledSvg = fallbackSvg;
    }
  },
  mounted() {
    this.getIcon({ type: this.type, name: this.name })
      .then(res => {
        this.icon = res;
        this.loadFile();
      })
      .catch(error => {
        console.log(error);
      });
  }
};
</script>
<style scoped>
.cause-icon {
  border: solid;
  border-width: 1px;
  overflow: hidden;
  transition: transform 0.2s ease;
  box-sizing: border-box;
}

.cause-icon:hover {
  transform: scale(1.05);
}

/* Ensure SVG content displays properly */
.cause-icon :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}

/* Remove background color for SVG icons */
.cause-icon:not(.fallback-icon):not(.loading-icon) {
  background-color: transparent !important;
}

.fallback-icon {
  position: relative;
}

.loading-icon {
  position: relative;
}

.fallback-text {
  color: white;
  font-weight: bold;
  font-size: 50%;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}
</style>
